import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/App";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import {
  Users, MessageCircle, Heart, Send, MapPin,
  CheckCircle, Lock, Plus, Trash2, Flag, ChevronDown, ChevronUp
} from "lucide-react";

export default function Community() {
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPost, setNewPost] = useState({ content: "", destination: "", post_type: "text" });
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [userRes, postsRes] = await Promise.all([
        fetch(`${API}/auth/me`, { credentials: "include" }),
        fetch(`${API}/community/posts`, { credentials: "include" })
      ]);
      
      if (userRes.ok) setUser(await userRes.json());
      if (postsRes.ok) setPosts(await postsRes.json());
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load posts. Please refresh.");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCreatePost = async () => {
    if (!newPost.content.trim()) {
      toast.error("Please write something");
      return;
    }
    
    try {
      const response = await fetch(`${API}/community/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newPost)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to create post");
      }
      
      toast.success("Post shared! 🎉");
      setShowCreateModal(false);
      setNewPost({ content: "", destination: "", post_type: "text" });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleDeletePost = async (postId) => {
    try {
      const response = await fetch(`${API}/community/posts/${postId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (!response.ok) throw new Error("Failed to delete post");
      toast.success("Post deleted");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleReportPost = async (postId) => {
    try {
      const response = await fetch(`${API}/community/posts/${postId}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: "inappropriate" })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to report");
      toast.success(data.message || "Post reported");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLike = async (postId) => {
    if (!user?.is_verified) {
      toast.error("Please verify your account to like posts");
      return;
    }
    
    try {
      const response = await fetch(`${API}/community/posts/${postId}/like`, {
        method: "POST",
        credentials: "include"
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to like post");
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleVerify = async () => {
    try {
      const response = await fetch(`${API}/community/verify`, {
        method: "POST",
        credentials: "include"
      });
      
      const result = await response.json();
      
      if (result.verified) {
        toast.success(result.message);
        fetchData();
      } else {
        toast.info(result.message);
      }
    } catch (error) {
      toast.error("Verification failed");
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="community-skeleton">
        {/* Skeleton Header */}
        <div className="bg-white border-b border-slate-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="h-6 w-32 bg-slate-200 rounded animate-pulse mb-1" />
              <div className="h-3 w-44 bg-slate-100 rounded animate-pulse" />
            </div>
            <div className="h-10 w-20 bg-slate-200 rounded-full animate-pulse" />
          </div>
        </div>
        <div className="px-6 py-6 space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-slate-200 animate-pulse" />
                <div>
                  <div className="h-4 w-28 bg-slate-200 rounded animate-pulse mb-1" />
                  <div className="h-3 w-20 bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
              <div className="space-y-2 mb-3">
                <div className="h-3 w-full bg-slate-100 rounded animate-pulse" />
                <div className="h-3 w-4/5 bg-slate-100 rounded animate-pulse" />
              </div>
              <div className="flex gap-4">
                <div className="h-4 w-12 bg-slate-100 rounded animate-pulse" />
                <div className="h-4 w-16 bg-slate-100 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        <BottomNav />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="community-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Community</h1>
            <p className="text-sm text-slate-500">Connect with fellow travelers</p>
          </div>
          {user?.is_verified ? (
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="savecation-button h-10 px-4"
              data-testid="create-post-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Share
            </Button>
          ) : (
            <Button 
              onClick={handleVerify}
              variant="outline"
              className="rounded-full h-10"
              data-testid="verify-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> Get Verified
            </Button>
          )}
        </div>
      </header>
      
      <main className="px-6 py-6">
        {/* Verification Notice */}
        {!user?.is_verified && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800">Verification Required</p>
                <p className="text-sm text-amber-700">
                  Complete a savings goal or make a payment to unlock posting, liking, and commenting.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Posts Feed */}
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-6">
              <Users className="w-12 h-12 text-sky-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">No posts yet!</h2>
            <p className="text-slate-500 mb-6">Be the first to share your travel story</p>
          </div>
        ) : (
          <div className="space-y-4 mb-24">
            {posts.map(post => (
              <PostCard
                key={post.post_id}
                post={post}
                onLike={() => handleLike(post.post_id)}
                onDelete={() => handleDeletePost(post.post_id)}
                onReport={() => handleReportPost(post.post_id)}
                canInteract={user?.is_verified}
                isOwner={user?.user_id === post.user_id}
              />
            ))}
          </div>
        )}
      </main>
      
      {/* Create Post Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Share with Community</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <textarea
                placeholder="Share your travel experience, tip, or question..."
                value={newPost.content}
                onChange={(e) => setNewPost({ ...newPost, content: e.target.value.slice(0, 500) })}
                maxLength={500}
                className="w-full h-32 p-4 rounded-2xl border-2 border-slate-100 bg-slate-50 resize-none focus:border-sky-400 focus:outline-none"
                data-testid="post-content-input"
              />
              <p className={`text-xs text-right mt-1 ${newPost.content.length >= 480 ? "text-red-400" : "text-slate-400"}`}>
                {newPost.content.length}/500
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Destination (optional)</label>
              <Input 
                placeholder="e.g., Bali, Indonesia"
                value={newPost.destination}
                onChange={(e) => setNewPost({ ...newPost, destination: e.target.value })}
                className="savecation-input"
                data-testid="post-destination-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Post Type</label>
              <div className="flex gap-2">
                {["text", "trip_report", "tip"].map(type => (
                  <button
                    key={type}
                    onClick={() => setNewPost({ ...newPost, post_type: type })}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      newPost.post_type === type
                        ? "bg-sky-500 text-white"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {type === "trip_report" ? "Trip Report" : type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <Button 
              onClick={handleCreatePost}
              className="savecation-button w-full"
              data-testid="submit-post-btn"
            >
              <Send className="w-4 h-4 mr-2" /> Share Post
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <BottomNav />
    </div>
  );
}

function PostCard({ post, onLike, onDelete, onReport, canInteract, isOwner }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const getTypeLabel = (type) => {
    if (type === "trip_report") return "Trip Report";
    if (type === "tip") return "Travel Tip";
    return null;
  };

  const toggleComments = async () => {
    if (!showComments && comments.length === 0) {
      setIsLoadingComments(true);
      try {
        const res = await fetch(`${API}/community/posts/${post.post_id}/comments`, { credentials: "include" });
        if (res.ok) setComments(await res.json());
      } catch {}
      setIsLoadingComments(false);
    }
    setShowComments(v => !v);
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API}/community/posts/${post.post_id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: commentText.trim() })
      });
      if (res.ok) {
        const newComment = await res.json();
        setComments(prev => [...prev, newComment]);
        setCommentText("");
      }
    } catch {}
    setIsSubmitting(false);
  };

  return (
    <div className="post-card" data-testid={`post-${post.post_id}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <img
            src={post.user_picture || "https://via.placeholder.com/40"}
            alt={post.user_name}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <p className="font-semibold text-slate-900">{post.user_name}</p>
            <p className="text-xs text-slate-500">{formatDate(post.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {getTypeLabel(post.post_type) && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-sky-100 text-sky-600 mr-1">
              {getTypeLabel(post.post_type)}
            </span>
          )}
          {isOwner ? (
            <button
              onClick={onDelete}
              className="p-1.5 text-slate-400 hover:text-rose-500 rounded-lg hover:bg-rose-50"
              title="Delete post"
              data-testid={`delete-post-${post.post_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={onReport}
              className="p-1.5 text-slate-400 hover:text-amber-500 rounded-lg hover:bg-amber-50"
              title="Report post"
              data-testid={`report-post-${post.post_id}`}
            >
              <Flag className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <p className="text-slate-800 mb-3 whitespace-pre-wrap">{post.content}</p>

      {/* Destination */}
      {post.destination && (
        <p className="text-sm text-sky-600 flex items-center gap-1 mb-3">
          <MapPin className="w-3 h-3" /> {post.destination}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-4 pt-3 border-t border-slate-100">
        <button
          onClick={onLike}
          disabled={!canInteract}
          className={`flex items-center gap-2 text-sm ${canInteract ? "text-slate-600 hover:text-rose-500" : "text-slate-400"}`}
          data-testid={`like-post-${post.post_id}`}
        >
          <Heart className="w-4 h-4" />
          <span>{post.likes}</span>
        </button>
        <button
          onClick={toggleComments}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-sky-500"
          data-testid={`comments-toggle-${post.post_id}`}
        >
          <MessageCircle className="w-4 h-4" />
          <span>{post.comments_count}</span>
          {showComments ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
          {isLoadingComments ? (
            <p className="text-xs text-slate-400 text-center py-2">Loading comments...</p>
          ) : comments.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-2">No comments yet. Be the first!</p>
          ) : (
            comments.map(c => (
              <div key={c.comment_id} className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-sky-600">{c.user_name?.charAt(0) || "?"}</span>
                </div>
                <div className="flex-1 bg-slate-50 rounded-xl px-3 py-2">
                  <p className="text-xs font-semibold text-slate-700">{c.user_name}</p>
                  <p className="text-sm text-slate-600">{c.content}</p>
                </div>
              </div>
            ))
          )}
          {canInteract && (
            <div className="flex gap-2">
              <input
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => e.key === "Enter" && submitComment()}
                placeholder="Add a comment..."
                className="flex-1 text-sm px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-sky-400"
                maxLength={300}
              />
              <button
                onClick={submitComment}
                disabled={isSubmitting || !commentText.trim()}
                className="p-2 bg-sky-500 hover:bg-sky-600 text-white rounded-xl disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
